import { createContext, useContext, useState, ReactNode } from 'react';
import { useUseCase } from './UseCaseContext';

interface FileSummary {
  fileName: string;
  fileSizeBytes: number;
  rowCount: number;
}

interface FleetSummary extends FileSummary {
  totalVehicles: number;
  dieselCount: number;
  electricCount: number;
  // ✅ ES mode specific fields
  stationCount?: number;
  uniqueStations?: string[];
  vehicleTypeBreakdown?: Record<string, number>;
}

interface AnalysisResponse {
  success: boolean;
  message: string;
  fileName: string;
  dateRange: string;
  period: string;
  generatedFiles: {
    telematics: string[];
    trips: string[];
    utilisation: string[];
    geographic: string[];
  };
  output: string;
  summary?: {
    telematics?: FileSummary | null;
    fleet?: FleetSummary | null;
  };
}

interface DataFeedState {
  // Upload states
  uploadingType: 'telematics' | 'fleet' | null;
  pendingTelematicsFile: File | null;
  analysisReady: boolean;
  
  // Analysis states
  analyzing: boolean;
  analysisResult: AnalysisResponse | null;
  analysisProgress: string;
  analysisStepIndex: number;
  
  // Error state
  error: string | null;
  
  // Actions
  setUploadingType: (type: 'telematics' | 'fleet' | null) => void;
  setPendingTelematicsFile: (file: File | null) => void;
  setAnalysisReady: (ready: boolean) => void;
  setAnalyzing: (analyzing: boolean) => void;
  setAnalysisResult: (result: AnalysisResponse | null) => void;
  setAnalysisProgress: (progress: string) => void;
  setAnalysisStepIndex: (index: number | ((prev: number) => number)) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const DataFeedContext = createContext<DataFeedState | undefined>(undefined);

export function DataFeedProvider({ children }: { children: ReactNode }) {
  const { useCase } = useUseCase();
  
  // ✅ Store state per mode to isolate Logistics and Emergency Services data
  const [logisticsState, setLogisticsState] = useState({
    uploadingType: null as 'telematics' | 'fleet' | null,
    pendingTelematicsFile: null as File | null,
    analysisReady: false,
    analyzing: false,
    analysisResult: null as AnalysisResponse | null,
    analysisProgress: '',
    analysisStepIndex: 0,
    error: null as string | null,
  });
  
  const [emergencyState, setEmergencyState] = useState({
    uploadingType: null as 'telematics' | 'fleet' | null,
    pendingTelematicsFile: null as File | null,
    analysisReady: false,
    analyzing: false,
    analysisResult: null as AnalysisResponse | null,
    analysisProgress: '',
    analysisStepIndex: 0,
    error: null as string | null,
  });
  
  // ✅ Get current state based on mode
  const currentState = useCase === 'emergency' ? emergencyState : logisticsState;
  const setCurrentState = useCase === 'emergency' ? setEmergencyState : setLogisticsState;
  
  // ✅ Reset state when switching modes (optional - comment out if you want to preserve state)
  // useEffect(() => {
  //   // State is already isolated per mode, so no need to reset
  // }, [useCase]);

  // React's useState setter already accepts both number and function, so we can use it directly
  // We just need to cast it to match our interface type
  const setAnalysisStepIndex = (value: number | ((prev: number) => number)) => {
    setCurrentState(prev => ({
      ...prev,
      analysisStepIndex: typeof value === 'function' ? (value as (prev: number) => number)(prev.analysisStepIndex) : value
    }));
  };

  const reset = () => {
    setCurrentState({
      uploadingType: null,
      pendingTelematicsFile: null,
      analysisReady: false,
      analyzing: false,
      analysisResult: null,
      analysisProgress: '',
      analysisStepIndex: 0,
      error: null,
    });
  };

  return (
    <DataFeedContext.Provider
      value={{
        uploadingType: currentState.uploadingType,
        pendingTelematicsFile: currentState.pendingTelematicsFile,
        analysisReady: currentState.analysisReady,
        analyzing: currentState.analyzing,
        analysisResult: currentState.analysisResult,
        analysisProgress: currentState.analysisProgress,
        analysisStepIndex: currentState.analysisStepIndex,
        error: currentState.error,
        setUploadingType: (type) => setCurrentState(prev => ({ ...prev, uploadingType: type })),
        setPendingTelematicsFile: (file) => setCurrentState(prev => ({ ...prev, pendingTelematicsFile: file })),
        setAnalysisReady: (ready) => setCurrentState(prev => ({ ...prev, analysisReady: ready })),
        setAnalyzing: (analyzing) => setCurrentState(prev => ({ ...prev, analyzing })),
        setAnalysisResult: (result) => setCurrentState(prev => ({ ...prev, analysisResult: result })),
        setAnalysisProgress: (progress) => setCurrentState(prev => ({ ...prev, analysisProgress: progress })),
        setAnalysisStepIndex,
        setError: (error) => setCurrentState(prev => ({ ...prev, error })),
        reset,
      }}
    >
      {children}
    </DataFeedContext.Provider>
  );
}

export function useDataFeed() {
  const context = useContext(DataFeedContext);
  if (!context) {
    throw new Error('useDataFeed must be used within a DataFeedProvider');
  }
  return context;
}

